import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {
  CardMetadataLookupResponse,
  CheckoutError,
  Component,
  CustomTranslations,
  HandleClickResponse,
  HandleSubmitResponse,
  loadCheckoutWebComponents,
  OnAuthorizedResponse,
  OnCardBinChangedResponse,
  OnTokenizedResponse,
  PaymentSessionResponse,
  PayPaymentSessionSuccessfulResponse,
  SubmitData,
  TokenizeResult,
} from '@checkout.com/checkout-web-components';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router';
import {useSearchParams} from 'react-router-dom';

import {
  CPaymentLinkStatuses,
  PaymentLinkUtm,
} from '@/app/main/paymentLink/models/constants';
import {
  IPaymentLinkV2ValidProps,
  PaymentLinkSuccessNavState,
} from '@/app/main/paymentLink/v2/pages/paymentLinkV2/models/interfaces/paymentLinkV2.interfaces';
import {IFamilyBillingProfile} from '@/app/main/splits/interfaces';
import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {SnackbarType} from '@/app/shared-components/Snackbar/SnackbarTypes';
import {useLoading} from '@/app/zustand/useLoading';
import {ENV} from '@/constants/EnvConstants';
import {PROJECT_TAGS} from '@/constants/ProjectsConstants';
import {resolveApplePayVisible} from '@/dataSource/src/api/firebase/applePayFlag';
import {useRemoteFeatureFlags} from '@/dataSource/src/api/firebase/useRemoteFeatureFlags';
import {useGroupsRepository} from '@/dataSource/src/api/groups/useGroupsRepository';
import {PaymentLinkFlows} from '@/dataSource/src/api/payment-link/constants';
import {IFee} from '@/dataSource/src/api/payment-link/entities';
// eslint-disable-next-line import/order
import {usePaymentLinkTrackers} from '@/dataSource/src/api/payment-link/trackers';

import {buildPaymentLinkPayload} from '@/dataSource/src/api/payment-link/utils/buildPaymentLinkPayload';
import {
  ICreatePaymentSessionResponse,
  IPaymentSessionCheckout,
  ISubmitPaymentSessionResponse,
} from '@/dataSource/src/api/payment-link/v2/models';
import {
  DestinationAccountTypes,
  MaxAmountTypes,
  PaymentLinkV2Types,
  PaymentMethodType,
  PaymentMethodTypes,
} from '@/dataSource/src/api/payment-link/v2/types';
import {usePaymentLinkV2Repository} from '@/dataSource/src/api/payment-link/v2/useRepository';
import {useUsersRepository} from '@/dataSource/src/api/users/useRepository';
import {IEventBaseDataSchema} from '@/services/segment/models/TrackingManagerEventModel';
import {WEB_VIEW_MESSAGES} from '@/services/webview/constants';
import {COLORS} from '@/styles/colors';
import {isProduction} from '@/utils/environment';
import logError, {logBreadcrumb} from '@/utils/errors/logError';
import {getFromStorage} from '@/utils/getFromStorage';

import {
  CHECKOUT_RESPONSE_DATA,
  PAYMENT_LINK_V2_FEATURES,
  PAYMENT_LINK_V2_QUERY_PARAMS,
  SESSION_STORAGE_PAYMENT_DATA,
} from '../../models/constants/constants';

import {BillingAddressFormRef} from './components/BillingAddressForm/BillingAddressForm';
import {DEFAULT_COUNTRY} from './components/BillingAddressForm/models/constants';
import {BillingAddressFormValues} from './components/BillingAddressForm/models/interfaces';
import {PAYMENT_METHODS} from './components/PaymentMethodsSelector/config';
import {
  CardTypes,
  DEFAULT_PAYMENT_METHODS,
  PaymentMethodV2,
  PaymentMethodV2Type,
} from './components/PaymentMethodsSelector/models/types';
import {
  CHECKOUT_BUTTON_APPLEPAY_ID,
  CHECKOUT_BUTTON_GOOGLEPAY_ID,
  REDIRECT_URLS,
} from './constants/constants';
import {useApplePaySession} from './useApplePaySession';
import {getPaymentTotal} from './utils/paymentFees';

export const defaultAmountQR = 20;
export const maxAmountDefault = 200;

export function useActions(props: IPaymentLinkV2ValidProps) {
  const {t} = useTranslation();
  const {flagsMap, getApplePayFlagState} = useRemoteFeatureFlags();
  const isSimplifiedBilling = flagsMap.paymentLinkV2SimplifiedBilling;
  const {profile} = useUsersRepository();
  const {handleConfirmSettlement} = useGroupsRepository();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const paymentSessionId = searchParams.get(
    CHECKOUT_RESPONSE_DATA.PAYMENT_SESSION_ID,
  );
  const sessionId = searchParams.get(CHECKOUT_RESPONSE_DATA.SESSION_ID);
  const paymentId = searchParams.get(CHECKOUT_RESPONSE_DATA.PAYMENT_ID);
  const groupId = searchParams.get(PAYMENT_LINK_V2_QUERY_PARAMS.GROUP_ID);
  const fromParticipantId = searchParams.get(
    PAYMENT_LINK_V2_QUERY_PARAMS.FROM_PARTICIPANT_ID,
  );
  const toParticipantId = searchParams.get(
    PAYMENT_LINK_V2_QUERY_PARAMS.TO_PARTICIPANT_ID,
  );

  const payerMetadata: IFamilyBillingProfile = useMemo(() => {
    if (profile) {
      return {
        firstName: profile?.firstName,
        lastName: profile?.lastName,
        email: profile?.email,
        phone: profile?.phone,
        additionalAddress: profile?.billingAddress?.additional_address,
        city: profile?.billingAddress?.city,
        state: profile?.billingAddress?.state,
        zipCode: profile?.billingAddress?.zip_code,
        country: profile?.billingAddress?.country,
        line: profile?.billingAddress?.line,
        profilePicture: profile?.metadata?.PROFILE_PICTURE,
      };
    }
    return JSON.parse(
      searchParams.get(PAYMENT_LINK_V2_QUERY_PARAMS.PAYER_METADATA) ?? '{}',
    );
  }, [profile, searchParams]);

  const originFeature = useMemo(
    () => searchParams.get(PAYMENT_LINK_V2_QUERY_PARAMS.FEATURE),
    [searchParams],
  );

  const isGroups = useMemo(
    () => originFeature === PAYMENT_LINK_V2_FEATURES.GROUPS,
    [originFeature],
  );

  const {startLoading, stopLoading} = useLoading();

  const isLink = useMemo(() => !!props.single_payment, [props.single_payment]);

  const paymentLinkFlow = useMemo(
    () => (isLink ? PaymentLinkFlows.LINK : PaymentLinkFlows.QR),
    [isLink],
  );

  const translations: CustomTranslations = useMemo(() => {
    return {
      'en-US': {
        'pay_button.pay_with': t('payment_link_v2.pay_button'),
      },
    };
  }, [t]);

  const isCampaign = props.type === PaymentLinkV2Types.CAMPAIGN;
  const isSavingsGoal = props.type === PaymentLinkV2Types.SAVING_GOAL;

  const [billingValues, setBillingValues] = useState<BillingAddressFormValues>({
    firstName: payerMetadata?.firstName ?? '',
    lastName: payerMetadata?.lastName ?? '',
    email: payerMetadata?.email ?? '',
    phone: payerMetadata?.phone ?? '',
    line1: payerMetadata?.line ?? '',
    line2: payerMetadata?.additionalAddress ?? '',
    city: payerMetadata?.city ?? '',
    state: payerMetadata?.state ?? '',
    postalCode: payerMetadata?.zipCode ?? '',
    country: payerMetadata?.country ?? DEFAULT_COUNTRY,
  });

  const billingFormRef = useRef<BillingAddressFormRef>(null);

  const cardRef = useRef<HTMLDivElement>(null);
  const applePayRef = useRef<HTMLDivElement>(null);
  const googlePayRef = useRef<HTMLDivElement>(null);
  const [selectedMethod, setSelectedMethod] =
    useState<PaymentMethodV2Type | null>(null);
  const [selectedMethodFee, setSelectedMethodFee] =
    useState<PaymentMethodType | null>(null);

  const {
    trackSelectChangeAmountPaymentLink,
    trackViewRequestMoneyErrorScreen,
    trackViewRequestMoneyPaymentRequest,
    trackSelectRequestMoneyPaymentOption,
    trackSelectRequestMoneyPaymentInfoConfirm,
    trackViewRequestMoneyPersonalInfo,
    trackSelectRequestMoneyPersonalInfoContinue,
    trackViewRequestMoneyPaymentInfo,
    trackCheckoutWebComponentOnReady,
    trackCheckoutWebComponentOnSubmit,
    trackCheckoutWebComponentOnClick,
    trackCheckoutWebComponentOnAuthorized,
    trackCheckoutWebComponentOnTokenized,
  } = usePaymentLinkTrackers();

  const {
    handlerCreatePaymentSession,
    handlerSubmitPaymentSession,
    handlerGetLimitsFees,
    handlerValidateApplePayMerchant,
    handlerSubmitApplePayPayment,
    limitsFees,
    isGetLimitsFeesLoading,
  } = usePaymentLinkV2Repository();

  const [, setIsSelectTabAmount] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState<number>(
    props?.amount ?? defaultAmountQR,
  );

  useEffect(() => {
    setSelectedAmount(props?.amount ?? defaultAmountQR);
  }, [props?.amount]);

  const [, setSelectedFee] = useState<IFee>(undefined);

  const [goalNote, setGoalNote] = useState('');

  const mapTrackingPayload = useCallback(
    (paymentMethod: string): IEventBaseDataSchema => {
      return {
        project_tag: PROJECT_TAGS.PAYMENT_LINK,
        user: {
          id: props.receiverUserId,
        },
        Fintech: {
          payment_link: {
            flow: paymentLinkFlow,
            payment_method: paymentMethod,
          },
          payment: {
            link: buildPaymentLinkPayload({
              id: props.paymentLinkId,
              type: props.type,
              campaignConfiguration: props.campaignConfiguration,
              savingsGoalConfiguration: props.savingsGoalConfiguration,
            }),
          },
          transaction: {
            method: paymentMethod,
            amount: selectedAmount?.toString(),
          },
          receiver: {
            user: {
              id: props.receiverUserId,
            },
          },
        },
      };
    },
    [
      paymentLinkFlow,
      props.receiverUserId,
      props.paymentLinkId,
      props.type,
      props.campaignConfiguration,
      props.savingsGoalConfiguration,
      selectedAmount,
    ],
  );

  const cardTypeRef = useRef<string | null>(null);

  const mapTrackingApiPayload = useCallback(
    (paymentMethod?: string) => ({
      flow: paymentLinkFlow,
      payment_method: paymentMethod ?? selectedMethod,
      receiver_user_id: props.receiverUserId,
      request_money_id: props.paymentLinkId,
      link_type: props.type,
      campaign_configuration: props.campaignConfiguration,
      savings_goal_configuration: props.savingsGoalConfiguration,
      amount: selectedAmount,
    }),
    [
      paymentLinkFlow,
      selectedMethod,
      props.receiverUserId,
      props.paymentLinkId,
      props.type,
      props.campaignConfiguration,
      props.savingsGoalConfiguration,
      selectedAmount,
    ],
  );

  // Personal info (billing) form becomes visible when a payment method is selected
  useEffect(() => {
    if (!selectedMethod) return;
    trackViewRequestMoneyPersonalInfo(mapTrackingApiPayload());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMethod]);

  const [paymentSessionCheckout, setPaymentSessionCheckout] =
    useState<IPaymentSessionCheckout | null>(null);

  const [paymentSessionModak, setPaymentSessionModak] =
    useState<ICreatePaymentSessionResponse | null>(null);

  const checkoutMaxAmount = useMemo(() => {
    return (
      limitsFees?.max_amounts?.find(
        item => item.type === MaxAmountTypes.CHECKOUT,
      )?.value ?? maxAmountDefault
    );
  }, [limitsFees]);

  const disableButtonContinue = useMemo(() => {
    return selectedAmount === 0 || selectedAmount > checkoutMaxAmount;
  }, [checkoutMaxAmount, selectedAmount]);

  // Apple Pay authorizes an exact amount in the sheet, so also block until the
  // fees are loaded — otherwise the total falls back to the base amount (no
  // fee) and would mismatch what the backend charges.
  const disableApplePayButton = useMemo(
    () =>
      disableButtonContinue ||
      isGetLimitsFeesLoading ||
      !limitsFees?.fees?.length,
    [disableButtonContinue, isGetLimitsFeesLoading, limitsFees],
  );

  const handleEditAddressBackButton = useCallback(() => {
    setPaymentSessionCheckout(null);
  }, [setPaymentSessionCheckout]);

  const handleSelectMethod = useCallback(
    (id: PaymentMethodV2Type) => {
      setSelectedMethod(id);
      switch (id) {
        case PaymentMethodV2.APPLEPAY:
          setSelectedMethodFee(PaymentMethodTypes.APPLE);
          break;
        case PaymentMethodV2.GOOGLEPAY:
          setSelectedMethodFee(PaymentMethodTypes.GOOGLE);
          break;
        case PaymentMethodV2.CARD:
          setSelectedMethodFee(PaymentMethodTypes.CREDIT);
          break;
      }
      trackSelectRequestMoneyPaymentOption(mapTrackingPayload(id));
    },
    [mapTrackingPayload, trackSelectRequestMoneyPaymentOption],
  );
  const handleCreatePaymentSession = useCallback(
    async (billingInfo?: BillingAddressFormValues) => {
      const BASE_URL = window.location.origin;
      const successPath = `${REDIRECT_URLS.SUCCESS(originFeature)}${isCampaign ? `?${PAYMENT_LINK_V2_QUERY_PARAMS.FUND_RAISE}` : ''}`;
      const urls = {
        success_url: `${BASE_URL}/${successPath}`,
        failure_url: `${BASE_URL}/${REDIRECT_URLS.FAILURE(props.paymentLinkId)}`,
      };
      const response: ICreatePaymentSessionResponse =
        await handlerCreatePaymentSession(
          {
            risk: {
              device_session_id: '', //deviceSessionId,
            },
            amount: selectedAmount,
            account_type:
              props.destinationAccountType ?? DestinationAccountTypes.CHECKING,
            success_url: urls.success_url,
            failure_url: urls.failure_url,

            address: {
              line1: billingInfo.line1 ?? '',
              line2: billingInfo.line2 ?? '',
              city: billingInfo.city ?? '',
              state: billingInfo.state ?? '',
              postal_code: billingInfo.postalCode ?? '',
              country: billingInfo.country ?? DEFAULT_COUNTRY,
            },
            first_name: billingInfo.firstName,
            last_name: billingInfo.lastName,
            email: billingInfo.email,
            phone: isSimplifiedBilling ? undefined : billingInfo.phone,
            payment_link_id: props.paymentLinkId,
            payment_methods: selectedMethod
              ? [selectedMethod]
              : DEFAULT_PAYMENT_METHODS,
            memo: goalNote || undefined,
          },
          {
            flow: paymentLinkFlow,
            payment_method:
              selectedMethod || DEFAULT_PAYMENT_METHODS.toString(),
            receiver_user_id: props.receiverUserId,
            request_money_id: props.paymentLinkId,
            link_type: props.type,
            campaign_configuration: props.campaignConfiguration,
          },
        );

      if (!response) return;
      setPaymentSessionModak(response);
      const newPaymentSessionCheckout: IPaymentSessionCheckout = {
        id: response.payment_session_id,
        payment_session_token: response.payment_session_token,
      };
      setPaymentSessionCheckout(newPaymentSessionCheckout);
    },
    [
      selectedMethod,
      originFeature,
      props.paymentLinkId,
      props.receiverUserId,
      props.type,
      props.campaignConfiguration,
      props.destinationAccountType,
      handlerCreatePaymentSession,
      selectedAmount,
      paymentLinkFlow,
      isSimplifiedBilling,
      isCampaign,
      goalNote,
    ],
  );

  const submitBilling = useCallback(
    (billingInfo: BillingAddressFormValues) => {
      trackSelectRequestMoneyPersonalInfoContinue(mapTrackingApiPayload());
      handleCreatePaymentSession(billingInfo);
    },
    [
      handleCreatePaymentSession,
      mapTrackingApiPayload,
      trackSelectRequestMoneyPersonalInfoContinue,
    ],
  );

  const handleSubmitPaymentSession = useCallback(
    async (
      submitData: string,
      paymentMethodUsed: string,
    ): Promise<ISubmitPaymentSessionResponse | null> => {
      if (
        !paymentSessionModak?.payment_session_id ||
        !paymentSessionModak?.checkout_transaction_id
      )
        return null;

      let cardTypeForUse: string | null = null;
      if (paymentMethodUsed === PaymentMethodV2.CARD) {
        cardTypeForUse = cardTypeRef.current;
        if (!cardTypeForUse) {
          showSnackbar({
            variant: SnackbarType.ERROR,
            summary: 'Error',
            message: 'card type empty',
          });
          return null;
        }
      }

      const response = await handlerSubmitPaymentSession(
        {
          payment_session_id: paymentSessionModak?.payment_session_id,
          submitPaymentSessionData: {
            payment_session_data: submitData,
            payment_method: paymentMethodUsed,
            card_type: cardTypeForUse,
            checkout_transaction_id:
              paymentSessionModak?.checkout_transaction_id,
            payment_link_id: props.paymentLinkId,
            amount: selectedAmount,
          },
        },
        {
          flow: paymentLinkFlow,
          payment_method: paymentMethodUsed,
          receiver_user_id: props.receiverUserId,
          request_money_id: props.paymentLinkId,
          card_type: cardTypeForUse || undefined,
          amount: selectedAmount,
          link_type: props.type,
          campaign_configuration: props.campaignConfiguration,
          savings_goal_configuration: props.savingsGoalConfiguration,
        },
      );

      return response;
    },
    [
      handlerSubmitPaymentSession,
      paymentLinkFlow,
      paymentSessionModak,
      props.paymentLinkId,
      props.receiverUserId,
      props.type,
      props.campaignConfiguration,
      props.savingsGoalConfiguration,
      selectedAmount,
    ],
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _autoTriggerPayButton = useCallback(() => {
    let containerId = '';
    switch (selectedMethod) {
      case PaymentMethodV2.GOOGLEPAY:
        containerId = CHECKOUT_BUTTON_GOOGLEPAY_ID;
        break;
      case PaymentMethodV2.APPLEPAY:
        containerId = CHECKOUT_BUTTON_APPLEPAY_ID;
        break;
      default:
        return;
    }

    const btn = document.querySelector<HTMLButtonElement>(
      `${containerId} button, ${containerId} [role="button"]`,
    );

    if (!btn) return;

    btn.click();
  }, [selectedMethod]);

  const saveSessionPaymentData = useCallback(
    (transactionId: string) => {
      const lastPaymentData: PaymentLinkSuccessNavState = {
        paymentLinkId: props.paymentLinkId,
        receiverUserId: props.receiverUserId,
        paymentMethod: selectedMethod,
        kidName: props.name,
        flow: paymentLinkFlow,
        transactionId: transactionId,
        amount: selectedAmount,
        utmCampaign: isLink
          ? PaymentLinkUtm.Campaign.LINK
          : PaymentLinkUtm.Campaign.QR,
        cardType: cardTypeRef.current || undefined,
        isCampaign,
        campaignTitle: props.campaignConfiguration?.title,
        linkType: props.type,
        campaignConfiguration: props.campaignConfiguration,
        isSavingsGoal,
        goalName: props.savingsGoalConfiguration?.name,
        goalIcon: props.savingsGoalConfiguration?.icon,
      };

      sessionStorage.setItem(
        SESSION_STORAGE_PAYMENT_DATA,
        JSON.stringify(lastPaymentData),
      );
    },
    [
      isLink,
      paymentLinkFlow,
      props?.name,
      props?.paymentLinkId,
      props?.receiverUserId,
      props?.type,
      selectedAmount,
      selectedMethod,
      isCampaign,
      props?.campaignConfiguration,
      isSavingsGoal,
      props?.savingsGoalConfiguration,
    ],
  );

  const handleOnSubmitCheckout = useCallback(
    async (
      component: Component,
      submitData: SubmitData,
    ): Promise<HandleSubmitResponse> => {
      trackSelectRequestMoneyPaymentInfoConfirm(
        mapTrackingPayload(selectedMethod),
      );
      trackCheckoutWebComponentOnSubmit({
        flow: paymentLinkFlow,
        payment_method: selectedMethod,
        receiver_user_id: props.receiverUserId,
        request_money_id: props.paymentLinkId,
        card_type: cardTypeRef.current || undefined,
      });
      const responseSubmit = await handleSubmitPaymentSession(
        submitData.session_data,
        component.type,
      );
      if (!responseSubmit) {
        const customError = new Error('Payment submission failed');
        const logErrorData = {
          checkoutComponent: component,
          componentData: {
            selectedType: component?.selectedType,
            type: component?.type,
            name: component?.name,
          },
        };
        logError(customError, {
          customTitle: 'PaymentLinkV2 onSubmit failed',
          fileName: 'PaymentLinkV2Valid.tsx',
          functionName: 'handleOnSubmitCheckout',
          data: logErrorData,
          extraTags: {
            payment_link_id: props.paymentLinkId,
            'user.id': props.receiverUserId,
          },
        });
        return Promise.reject(customError);
      }

      const submitResponse: HandleSubmitResponse = {
        id: responseSubmit.payment_id,
        status: responseSubmit.status,
        type: component.type,
        action: responseSubmit.action,
      };

      saveSessionPaymentData(responseSubmit.payment_id);
      return submitResponse;
    },
    [
      handleSubmitPaymentSession,
      paymentLinkFlow,
      props?.paymentLinkId,
      props?.receiverUserId,
      saveSessionPaymentData,
      selectedMethod,
      trackCheckoutWebComponentOnSubmit,
      trackSelectRequestMoneyPaymentInfoConfirm,
      mapTrackingPayload,
    ],
  );

  const handleOnCardBinChanged = useCallback(
    async (
      _component: Component,
      cardMetadata: CardMetadataLookupResponse,
    ): Promise<OnCardBinChangedResponse | void> => {
      if (cardMetadata?.card_type === CardTypes.CREDIT) {
        setSelectedMethodFee(PaymentMethodTypes.CREDIT);
      } else {
        setSelectedMethodFee(PaymentMethodTypes.DEBIT);
      }

      if (cardMetadata?.card_type) {
        cardTypeRef.current = cardMetadata.card_type;
      }

      if (cardMetadata?.card_type === CardTypes.PREPAID) {
        return {
          continue: false,
          errorMessage: t('payment_link_v2.card_prepaid_not_allowed'),
        };
      }

      return {
        continue: true,
      };
    },
    [t],
  );

  const handleOnReady = useCallback(() => {
    trackCheckoutWebComponentOnReady({
      flow: paymentLinkFlow,
      payment_method: selectedMethod,
      receiver_user_id: props.receiverUserId,
      request_money_id: props.paymentLinkId,
    });
    // Payment info (checkout) form is rendered and visible
    trackViewRequestMoneyPaymentInfo(mapTrackingApiPayload());
    stopLoading();
  }, [
    paymentLinkFlow,
    props.paymentLinkId,
    props.receiverUserId,
    selectedMethod,
    stopLoading,
    trackCheckoutWebComponentOnReady,
    trackViewRequestMoneyPaymentInfo,
    mapTrackingApiPayload,
  ]);

  const handleReturnToWebView = useCallback(() => {
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        status: WEB_VIEW_MESSAGES.PAYMENT_LINK_PAYMENT_COMPLETED,
        amount: selectedAmount,
      }),
    );
  }, [selectedAmount]);

  const handleSettlement = useCallback(() => {
    handleConfirmSettlement(groupId, {
      settlements: [
        {
          from: fromParticipantId,
          to: toParticipantId,
          amount: selectedAmount,
          group_id: groupId,
          settled_by: fromParticipantId,
        },
      ],
    });
  }, [
    fromParticipantId,
    groupId,
    handleConfirmSettlement,
    selectedAmount,
    toParticipantId,
  ]);

  const handleNavigationAfterPayment = useCallback(
    (lastPaymentData: PaymentLinkSuccessNavState) => {
      if (
        isGroups &&
        typeof window.ReactNativeWebView !== 'undefined' &&
        window.ReactNativeWebView
      ) {
        handleReturnToWebView();
        return;
      }
      if (isGroups) {
        lastPaymentData.settleGroupSplit = {
          amount: selectedAmount,
          groupId: groupId,
          fromSplitUserId: fromParticipantId,
          toSplitUserId: toParticipantId,
        };
        handleSettlement();
      }
      navigate(
        `/${REDIRECT_URLS.SUCCESS(originFeature)}?${searchParams.toString()}`,
        {
          state: lastPaymentData,
        },
      );
    },
    [
      fromParticipantId,
      groupId,
      handleReturnToWebView,
      handleSettlement,
      isGroups,
      navigate,
      originFeature,
      searchParams,
      selectedAmount,
      toParticipantId,
    ],
  );

  const handleOnPaymentCompleted = useCallback(
    (_component: Component, payment: PayPaymentSessionSuccessfulResponse) => {
      const lastPaymentData: PaymentLinkSuccessNavState = {
        paymentLinkId: props.paymentLinkId,
        receiverUserId: props.receiverUserId,
        paymentMethod: selectedMethod,
        kidName: props.name,
        flow: paymentLinkFlow,
        transactionId: payment?.id,
        amount: selectedAmount,
        utmCampaign: isLink
          ? PaymentLinkUtm.Campaign.LINK
          : PaymentLinkUtm.Campaign.QR,
        cardType: cardTypeRef.current || undefined,
        otpVerified: true,
        isCampaign,
        campaignTitle: props.campaignConfiguration?.title,
        linkType: props.type,
        campaignConfiguration: props.campaignConfiguration,
        isSavingsGoal,
        goalName: props.savingsGoalConfiguration?.name,
        goalIcon: props.savingsGoalConfiguration?.icon,
      };

      handleNavigationAfterPayment(lastPaymentData);
    },
    [
      props.paymentLinkId,
      props.receiverUserId,
      props.name,
      props.type,
      selectedMethod,
      paymentLinkFlow,
      selectedAmount,
      isLink,
      handleNavigationAfterPayment,
      isCampaign,
      props.campaignConfiguration,
      isSavingsGoal,
      props.savingsGoalConfiguration,
    ],
  );

  const handleOnError = useCallback(
    (component: Component, error: CheckoutError) => {
      stopLoading();
      const isPrepaid = cardTypeRef?.current === CardTypes.PREPAID;
      if (!isPrepaid) {
        const logErrorData = {
          checkoutComponent: component,
          componentData: {
            selectedType: component?.selectedType,
            type: component?.type,
            name: component?.name,
            cardType: cardTypeRef.current,
          },
        };
        logError(error, {
          customTitle: 'PaymentLinkV2 onError',
          fileName: 'PaymentLinkV2Valid.tsx',
          functionName: 'handleOnError',
          data: logErrorData,
          extraTags: {
            payment_link_id: props.paymentLinkId,
            'user.id': props.receiverUserId,
          },
        });
        showSnackbar({
          variant: SnackbarType.ERROR,
          summary: t('payment_link.error_payment.title'),
          message: t('payment_link.error_payment.message'),
        });
      } else {
        showSnackbar({
          variant: SnackbarType.INFO,
          summary: t('payment_link.invalid_prepaid_card.title'),
          message: t('payment_link.invalid_prepaid_card.message'),
        });
      }

      trackViewRequestMoneyErrorScreen({
        receiver_user_id: props.receiverUserId,
        request_money_id: props.paymentLinkId,
        flow: paymentLinkFlow,
        payment_method: selectedMethod,
        card_type: cardTypeRef.current || undefined,
      });
    },
    [
      paymentLinkFlow,
      props.paymentLinkId,
      props.receiverUserId,
      selectedMethod,
      stopLoading,
      t,
      trackViewRequestMoneyErrorScreen,
    ],
  );

  const onHandleClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async (_component: Component): Promise<HandleClickResponse | void> => {
      trackCheckoutWebComponentOnClick({
        flow: paymentLinkFlow,
        payment_method: selectedMethod,
        receiver_user_id: props.receiverUserId,
        request_money_id: props.paymentLinkId,
      });
      return {
        continue: true,
      };
    },
    [
      paymentLinkFlow,
      props.paymentLinkId,
      props.receiverUserId,
      selectedMethod,
      trackCheckoutWebComponentOnClick,
    ],
  );

  const handleOnTokenized = useCallback(
    async (
      component: Component,
      tokenizeResult: TokenizeResult,
    ): Promise<OnTokenizedResponse | void> => {
      trackCheckoutWebComponentOnTokenized({
        flow: paymentLinkFlow,
        payment_method: selectedMethod,
        receiver_user_id: props.receiverUserId,
        request_money_id: props.paymentLinkId,
        card_type: cardTypeRef.current || undefined,
      });
      logBreadcrumb({
        category: `PaymentLink.useActions.handleOnTokenized`,
        type: 'query',
        message: 'Fund with Card',
        data: {
          component: component,
          tokenizeResult: tokenizeResult,
          paymentLinkId: props.paymentLinkId,
          userId: props.receiverUserId,
          cardType: cardTypeRef.current,
        },
      });
      return {
        continue: true,
      };
    },
    [
      trackCheckoutWebComponentOnTokenized,
      paymentLinkFlow,
      selectedMethod,
      props.receiverUserId,
      props.paymentLinkId,
    ],
  );

  const handleOnAuthorized = useCallback(
    async (
      component: Component,
      authorizeResult: unknown,
    ): Promise<OnAuthorizedResponse | void> => {
      trackCheckoutWebComponentOnAuthorized({
        flow: paymentLinkFlow,
        payment_method: selectedMethod,
        receiver_user_id: props.receiverUserId,
        request_money_id: props.paymentLinkId,
        card_type: cardTypeRef.current || undefined,
      });
      logBreadcrumb({
        category: `PaymentLink.useActions.handleOnAuthorized`,
        type: 'user',
        message: 'Fund with Apple pay or google pay',
        data: {
          component: component,
          authorizeResult: authorizeResult,
          paymentLinkId: props.paymentLinkId,
          userId: props.receiverUserId,
        },
      });
      return {
        continue: true,
      };
    },
    [
      paymentLinkFlow,
      props.paymentLinkId,
      props.receiverUserId,
      selectedMethod,
      trackCheckoutWebComponentOnAuthorized,
    ],
  );

  const handleApplePayValidateMerchant = useCallback(
    (): Promise<unknown> => handlerValidateApplePayMerchant(),
    [handlerValidateApplePayMerchant],
  );

  const handleApplePaySubmitPayment = useCallback(
    async (
      token: ApplePayJS.ApplePayPaymentToken,
      billingContact: ApplePayJS.ApplePayPaymentContact,
      shippingContact?: ApplePayJS.ApplePayPaymentContact,
    ) => {
      const paymentData = token.paymentData as {
        version: string;
        data: string;
        signature: string;
        header: {
          ephemeralPublicKey: string;
          publicKeyHash: string;
          transactionId: string;
        };
      };

      const response = await handlerSubmitApplePayPayment(
        {
          payment_link_id: props.paymentLinkId,
          amount: selectedAmount,
          currency: 'USD',
          token_data: {
            version: paymentData.version,
            data: paymentData.data,
            signature: paymentData.signature,
            header: {
              ephemeralPublicKey: paymentData.header.ephemeralPublicKey,
              publicKeyHash: paymentData.header.publicKeyHash,
              transactionId: paymentData.header.transactionId,
            },
          },
          payer_information: {
            first_name: billingContact.givenName ?? '',
            last_name: billingContact.familyName ?? '',
            // Apple delivers email/phone in shippingContact; fall back to billingContact.
            email:
              shippingContact?.emailAddress ||
              billingContact.emailAddress ||
              '',
            phone: shippingContact?.phoneNumber || billingContact.phoneNumber,
            address_lines: billingContact.addressLines ?? [],
            city: billingContact.locality ?? '',
            state: billingContact.administrativeArea ?? '',
            postal_code: billingContact.postalCode ?? '',
            country_code: billingContact.countryCode ?? 'US',
          },
          memo: goalNote || undefined,
        },
        {
          flow: paymentLinkFlow,
          payment_method: PaymentMethodV2.APPLEPAY,
          receiver_user_id: props.receiverUserId,
          request_money_id: props.paymentLinkId,
          amount: selectedAmount,
          link_type: props.type,
          campaign_configuration: props.campaignConfiguration,
          savings_goal_configuration: props.savingsGoalConfiguration,
        },
      );

      if (!response) throw new Error('Apple Pay payment submission failed');
      return response;
    },
    [
      handlerSubmitApplePayPayment,
      props.paymentLinkId,
      selectedAmount,
      goalNote,
      paymentLinkFlow,
      props.receiverUserId,
      props.type,
      props.campaignConfiguration,
      props.savingsGoalConfiguration,
    ],
  );

  const handleApplePaySuccess = useCallback(
    (paymentId: string) => {
      saveSessionPaymentData(paymentId);
      const lastPaymentData: PaymentLinkSuccessNavState = {
        paymentLinkId: props.paymentLinkId,
        receiverUserId: props.receiverUserId,
        paymentMethod: PaymentMethodV2.APPLEPAY,
        kidName: props.name,
        flow: paymentLinkFlow,
        transactionId: paymentId,
        amount: selectedAmount,
        utmCampaign: isLink
          ? PaymentLinkUtm.Campaign.LINK
          : PaymentLinkUtm.Campaign.QR,
        isCampaign,
        campaignTitle: props.campaignConfiguration?.title,
        linkType: props.type,
        campaignConfiguration: props.campaignConfiguration,
        isSavingsGoal,
        goalName: props.savingsGoalConfiguration?.name,
        goalIcon: props.savingsGoalConfiguration?.icon,
      };
      handleNavigationAfterPayment(lastPaymentData);
    },
    [
      handleNavigationAfterPayment,
      isCampaign,
      isLink,
      isSavingsGoal,
      paymentLinkFlow,
      props.campaignConfiguration,
      props.name,
      props.paymentLinkId,
      props.receiverUserId,
      props.savingsGoalConfiguration,
      props.type,
      saveSessionPaymentData,
      selectedAmount,
    ],
  );

  const handleApplePayError = useCallback(() => {
    showSnackbar({
      variant: SnackbarType.ERROR,
      summary: t('payment_link.error_payment.title'),
      message: t('payment_link.error_payment.message'),
    });
    trackViewRequestMoneyErrorScreen({
      receiver_user_id: props.receiverUserId,
      request_money_id: props.paymentLinkId,
      flow: paymentLinkFlow,
      payment_method: PaymentMethodV2.APPLEPAY,
    });
  }, [
    paymentLinkFlow,
    props.paymentLinkId,
    props.receiverUserId,
    t,
    trackViewRequestMoneyErrorScreen,
  ]);

  const applePaySessionHandlers = useMemo(
    () => ({
      validateMerchant: handleApplePayValidateMerchant,
      submitPayment: handleApplePaySubmitPayment,
      onSuccess: handleApplePaySuccess,
      onError: handleApplePayError,
    }),
    [
      handleApplePayError,
      handleApplePaySubmitPayment,
      handleApplePaySuccess,
      handleApplePayValidateMerchant,
    ],
  );

  const {enabled: applePayApiEnabled} = getApplePayFlagState(
    props.paymentLinkId,
  );

  // The Apple Pay sheet total must match the fee box ("You pay"): receiver
  // amount + processing fee, computed the same way as PaymentLinkV2LimitFeeBox.
  const applePayTotal = useMemo(
    () => getPaymentTotal(selectedAmount, PaymentMethodTypes.APPLE, limitsFees),
    [selectedAmount, limitsFees],
  );

  const {isAvailable: isApplePayAvailable, startSession: startApplePaySession} =
    useApplePaySession(
      Math.round(applePayTotal * 100),
      applePaySessionHandlers,
      applePayApiEnabled,
    );

  const paymentMethods = useMemo(
    () =>
      PAYMENT_METHODS.map(option =>
        option.id === PaymentMethodV2.APPLEPAY
          ? {
              ...option,
              visible: resolveApplePayVisible(
                applePayApiEnabled,
                isApplePayAvailable,
              ),
            }
          : option,
      ),
    [applePayApiEnabled, isApplePayAvailable],
  );

  const mountCheckoutWebComponents = useCallback(async () => {
    if (!paymentSessionCheckout) return;
    const paymentSessionCheckoutWebComponent: PaymentSessionResponse = {
      id: paymentSessionCheckout.id,
      payment_session_token: paymentSessionCheckout.payment_session_token,
    };

    startLoading();
    const checkout = await loadCheckoutWebComponents({
      publicKey: ENV.VITE_CHECKOUT_PUBLIC_KEY,
      environment: isProduction ? 'production' : 'sandbox',
      locale: 'en-US',
      translations,
      appearance: {
        colorAction: COLORS.ON_SURFACE_1,
        colorPrimary: COLORS.ON_SURFACE_VARIANT,
        borderRadius: ['8px', '100px'],
      },
      paymentSession: paymentSessionCheckoutWebComponent,
      handleSubmit: handleOnSubmitCheckout,
      onCardBinChanged: handleOnCardBinChanged,
      onReady: handleOnReady,
      onPaymentCompleted: handleOnPaymentCompleted,
      onError: handleOnError,
      handleClick: onHandleClick,
      onTokenized: handleOnTokenized,
      onAuthorized: handleOnAuthorized,
    });

    switch (selectedMethod) {
      case PaymentMethodV2.CARD: {
        const card = checkout.create(PaymentMethodV2.CARD);
        if (await card.isAvailable()) {
          card.mount(cardRef.current);
        } else {
          stopLoading();
        }
        break;
      }
      case PaymentMethodV2.APPLEPAY: {
        // Native Apple Pay renders its own button; the old Web Components Apple
        // Pay is retired, so never mount it here.
        stopLoading();
        break;
      }
      case PaymentMethodV2.GOOGLEPAY: {
        const googlepay = checkout.create(PaymentMethodV2.GOOGLEPAY);
        if (await googlepay.isAvailable()) {
          googlepay.mount(googlePayRef.current);
        } else {
          stopLoading();
        }
        break;
      }
    }
  }, [
    paymentSessionCheckout,
    startLoading,
    translations,
    handleOnSubmitCheckout,
    handleOnCardBinChanged,
    handleOnReady,
    handleOnPaymentCompleted,
    handleOnError,
    onHandleClick,
    handleOnTokenized,
    handleOnAuthorized,
    selectedMethod,
    stopLoading,
  ]);

  const handleOnCustomAmountClick = useCallback(() => {
    showSnackbar({
      variant: SnackbarType.INFO,
      summary: t('payment_link.custom_amount_coming_soon.title'),
      message: t('payment_link.custom_amount_coming_soon.message'),
    });
    trackSelectChangeAmountPaymentLink(
      props.receiverUserId,
      props.paymentLinkId,
    );
  }, [
    t,
    trackSelectChangeAmountPaymentLink,
    props.receiverUserId,
    props.paymentLinkId,
  ]);

  const handleTrackFailureRedirection = useCallback(() => {
    const failure = searchParams.get(PAYMENT_LINK_V2_QUERY_PARAMS.FAILURE);
    if (failure === 'true') {
      const paymentData = getFromStorage<PaymentLinkSuccessNavState>(
        SESSION_STORAGE_PAYMENT_DATA,
      );

      trackViewRequestMoneyErrorScreen({
        receiver_user_id: props.receiverUserId,
        request_money_id: props.paymentLinkId,
        flow: paymentLinkFlow,
        payment_method: paymentData?.paymentMethod,
        card_type: cardTypeRef.current || undefined,
        transaction_id: paymentData?.transactionId,
        paymentSessionId: paymentSessionId || undefined,
        sessionId: sessionId || undefined,
        paymentId: paymentId || undefined,
      });
      showSnackbar({
        variant: SnackbarType.ERROR,
        summary: t('payment_link.error_failure_redirect.title'),
        message: t('payment_link.error_failure_redirect.message'),
      });
      const error = new Error('Payment failure redirection');
      logError(error, {
        fileName: 'PaymentLinkV2Valid.tsx',
        functionName: 'useActions - useEffect - failure',
        customTitle: 'PaymentLinkV2 failure redirection 3ds',
        data: {
          paymentSessionId,
          sessionId,
          paymentId,
        },
        extraTags: {
          payment_link_id: props.paymentLinkId,
          'user.id': props.receiverUserId,
        },
      });
    }
  }, [
    paymentId,
    paymentLinkFlow,
    paymentSessionId,
    props?.paymentLinkId,
    props?.receiverUserId,
    searchParams,
    sessionId,
    t,
    trackViewRequestMoneyErrorScreen,
  ]);

  useEffect(() => {
    if (selectedAmount <= 0 || !billingValues?.line1) return;
    billingFormRef.current?.expand();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAmount]);

  useEffect(() => {
    if (!paymentSessionCheckout) return;
    mountCheckoutWebComponents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentSessionCheckout]);

  useEffect(() => {
    if (!limitsFees && !isGetLimitsFeesLoading) {
      handlerGetLimitsFees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limitsFees, isGetLimitsFeesLoading]);

  useEffect(() => {
    trackViewRequestMoneyPaymentRequest(
      {
        id: props.paymentLinkId,
        link_consumed: false,
        receiver_user: {
          id: props.receiverUserId,
          name: props.name,
        },
        single_payment: isLink,
        status: CPaymentLinkStatuses.VALID,
        amount: props.amount || 0,
        gif: props.gif,
        information: {
          gif: props.gif,
          note: props.note,
        },
      },
      {
        id: props.paymentLinkId,
        type: props.type,
        campaignConfiguration: props.campaignConfiguration,
        savingsGoalConfiguration: props.savingsGoalConfiguration,
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleTrackFailureRedirection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return {
    billingFormRef,
    billingValues,
    setBillingValues,
    submitBilling,
    handleEditAddressBackButton,
    disableButtonContinue,
    disableApplePayButton,
    paymentSessionCheckout,
    selectedAmount,
    setSelectedAmount,
    goalNote,
    setGoalNote,
    limitsFees,
    isLink,
    checkoutMaxAmount,
    setIsSelectTabAmount,
    setSelectedFee,
    handleOnCustomAmountClick,
    PAYMENT_METHODS: paymentMethods,
    selectedMethod,
    handleSelectMethod,
    applePayRef,
    googlePayRef,
    cardRef,
    selectedMethodFee,
    isSimplifiedBilling,
    applePayApiEnabled,
    isApplePayAvailable,
    startApplePaySession,
  };
}
